import { Hands, Results } from '@mediapipe/hands';
import { Camera } from '@mediapipe/camera_utils';
import { HandLandmarks, Landmark } from '../types';

export class MediaPipeService {
  private hands: Hands | null = null;
  private camera: Camera | null = null;
  private onResultsCallback: ((landmarks: HandLandmarks[]) => void) | null = null;

  async initialize(
    videoElement: HTMLVideoElement,
    onResults: (landmarks: HandLandmarks[]) => void
  ): Promise<void> {
    console.log('Initializing MediaPipe...');
    this.onResultsCallback = onResults;

    this.hands = new Hands({
      locateFile: (file) => {
        return `https://cdn.jsdelivr.net/npm/@mediapipe/hands/${file}`;
      }
    });

    this.hands.setOptions({
      maxNumHands: 2,
      modelComplexity: 1,
      minDetectionConfidence: 0.7,
      minTrackingConfidence: 0.7
    });

    this.hands.onResults(this.processResults.bind(this));
    console.log('MediaPipe hands configured');

    this.camera = new Camera(videoElement, {
      onFrame: async () => {
        if (this.hands) {
          await this.hands.send({ image: videoElement });
        }
      },
      width: 1280,
      height: 720
    });

    console.log('Starting camera...');
    await this.camera.start();
    console.log('MediaPipe initialized successfully');
  }

  private processResults(results: Results): void {
    if (!this.onResultsCallback) {
      return;
    }

    if (!results.multiHandLandmarks || results.multiHandLandmarks.length === 0) {
      // No hands detected
      this.onResultsCallback([]);
      return;
    }

    console.log('Processing', results.multiHandLandmarks.length, 'hands');

    const handLandmarks: HandLandmarks[] = [];

    for (let i = 0; i < results.multiHandLandmarks.length; i++) {
      const landmarks = results.multiHandLandmarks[i];
      const handedness = results.multiHandedness?.[i]?.label as 'Left' | 'Right' || 'Right';

      handLandmarks.push({
        landmarks: landmarks.map(lm => ({
          x: lm.x,
          y: lm.y,
          z: lm.z || 0
        })),
        handedness
      });
    }

    this.onResultsCallback(handLandmarks);
  }

  stop(): void {
    if (this.camera) {
      this.camera.stop();
    }
  }
}
